import React, { memo, useMemo, useCallback, useEffect } from 'react'
import {
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_LAST_MINUTE,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_ALWAYS_ON,
} from 'constants/offer'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import { buildSearchStringFromFilters } from 'lib/search/searchUtils'
import useBatchBedbankStickerRates from './useBatchBedbankStickerRates'
import { removeBedbankPropertiesWithLEOffer } from './utils'

import Heading from 'components/Luxkit/Typography/Heading'
import useOfferList from 'hooks/Offers/useOfferList'
import { detectOfferTypeFromId } from 'lib/offer/offerUtils'
import { skip, take, without } from 'lib/array/arrayUtils'
import useOffers from 'hooks/Offers/useOffers'
import { EmptyObject } from 'lib/object/objectUtils'
import { useAppSelector } from 'hooks/reduxHooks'
import { getGraphRecommendations } from 'selectors/recomendations/graphSelectors'
import { useInView } from 'react-intersection-observer'
import { isLpcHotelType } from 'lib/offer/offerTypes'
import { clickRecommendationOnLpcPage, viewRecommendationOnLpcPage } from 'analytics/optimizely/events'
import * as Analytics from 'analytics/analytics'
import { OfferListEvents, OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import { rem } from 'polished'
import styled from 'styled-components'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import OfferList from 'components/OfferList/OfferList'

const RecommendationsSection = styled(Pane)`
  padding: ${rem(40)} 0;
`

const emptyFilters: App.OfferListFilters = EmptyObject

interface Props {
  title?: string;
  offerId?: string;
  offerType?: App.OfferType;
  filters?: App.OfferListFilters;
  trackingDescription: string;
  useGraph?: boolean;
  skipFirstNOffers?: number;
  useList?: boolean;
}

const MINIMUM_OFFERS_TO_DISPLAY = 2
const MAX_OFFERS = 8

const RECOMMENDATION_TYPES: App.OfferType[] = [
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_LAST_MINUTE,
  'bedbank_hotel',
]

function OfferRecommendations(props: Props) {
  const {
    title = 'You may also like',
    offerId,
    offerType,
    filters = emptyFilters,
    trackingDescription,
    useGraph = false,
    skipFirstNOffers,
    useList = false,
  } = props

  const offerList = useOfferList({ ...filters, offerTypes: RECOMMENDATION_TYPES })
  const { offerIds: graphOfferIds, state: graphState } = useAppSelector(state => getGraphRecommendations(state, offerId))

  const offerIds = useMemo(() => {
    let offerCandidates = useGraph ? graphOfferIds : offerList.offerIds

    // remove source offer
    offerCandidates = without(offerCandidates, offerId ?? '')

    // skip first few offers if specified
    if (skipFirstNOffers) {
      offerCandidates = skip(offerCandidates, skipFirstNOffers)
    }

    // take max offers
    offerCandidates = take(offerCandidates, MAX_OFFERS)

    return offerCandidates
  }, [offerId, offerList.offerIds, useGraph, graphOfferIds, skipFirstNOffers])

  const [offers, offersFetching] = useOffers(offerIds)

  const algorithm = useGraph ? 'Graph' : (filters.propertyId ? 'Search Nearby' : 'Algolia')
  const tracking = useOfferListTracking(
    `Recommendations-${algorithm}-${trackingDescription}`, {
      ga: {
        category: 'Recommendations',
        label: `${algorithm}-${trackingDescription}`,
      },
    })

  const bedbankOfferIds = useMemo(() =>
    offerList.offerIds.filter(id => detectOfferTypeFromId(id) === 'bedbank_hotel')
  , [offerList.offerIds])

  const {
    hasLoading: bedbankRatesLoading,
  } = useBatchBedbankStickerRates(
    bedbankOfferIds,
    filters.rooms,
    filters.checkIn,
    filters.checkOut,
  )

  const isFetching = (
    (useGraph && graphState === 'loading') ||
    (!useGraph && offerList.fetching) ||
    offersFetching ||
    bedbankRatesLoading
  )

  const offersToDisplay = useMemo(() => {
    if (isFetching) {
      return []
    }

    return removeBedbankPropertiesWithLEOffer(offers)
  }, [isFetching, offers])

  const isSourceLpc = isLpcHotelType(offerType)

  const { ref, inView } = useInView({
    triggerOnce: true,
    skip: !isSourceLpc,
  })

  const onOfferListEvent = useCallback((eventType: OfferListEvents) => {
    if (eventType === OfferListEvents.productClick && isSourceLpc) {
      Analytics.track('click-recommendation-on-lpc-page', clickRecommendationOnLpcPage())
    }
  }, [isSourceLpc])

  useEffect(() => {
    if (inView && isSourceLpc) {
      Analytics.track('view-recommendation-on-lpc-page', viewRecommendationOnLpcPage())
    }
  }, [inView, isSourceLpc])

  const additionalQueryParams = useMemo(() => buildSearchStringFromFilters(filters), [filters])
  const toShowOfferList = useMemo<App.OfferList>(() => ({
    fetching: isFetching,
    offerIds,
    key: 'offerRecommendations',
  }), [isFetching, offerIds])

  if (offersToDisplay.length < MINIMUM_OFFERS_TO_DISPLAY || isFetching) {
    return null
  }

  return (
    <OfferListEventsProvider tracking={tracking} onListEvent={onOfferListEvent}>
      <RecommendationsSection ref={ref}>
        <VerticalSpacer gap={16}>
          <LayoutContainer>
            <Heading variant="heading3">{title}</Heading>
          </LayoutContainer>
          {!useList && <OfferListCarousel
            overrideOfferList={toShowOfferList}
            additionalOfferUrlQuery={additionalQueryParams}
          />}
          {useList && <OfferList
            offerlistOverride={toShowOfferList}
            tileStyle="search"
          />}
        </VerticalSpacer>
      </RecommendationsSection>
    </OfferListEventsProvider>
  )
}

export default memo(OfferRecommendations)
